import { useEffect, useState } from "react";
import { FeatureType } from "../type";
import getFeaturesListBySubId from "../function/getFeaturesListBySubId";
import getAllFeatures from "../function/getAllFeature";
import { Table, TableProps } from "antd";
import createFeature from "../function/createFeature";
import { toast } from "react-toastify";
import addFeatureToSubcription from "../function/addfeatureToSub";
import { createAndAddAdvancedFeature } from "../function/createAdvancedFeature";


interface EditFeatureOfSubModalProps {
    subcriptionId: number;
    onClose: () => void;
}
const tableCollumAll = [
    {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
    },
    {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
    },
    {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
    }
];

const tableCollumSub = [
    {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
    },
    {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
    },
    {
        title: 'Key',
        dataIndex: 'key',
        key: 'key',
    },
    {
        title: 'Value',
        dataIndex: 'number',
        key: 'number',
    },
]


const EditFeatureOfSubModal = ({ subcriptionId, onClose }: EditFeatureOfSubModalProps) => {
    const [feature, setFeature] = useState<FeatureType[]>([]);
    const [keyedFeature, setKeyedFeature] = useState<FeatureType[]>([]);
    const [allFeature, setAllFeature] = useState<FeatureType[]>([]);
    const [selectedRemoveFeature, setSelectedRemoveFeature] = useState<FeatureType[]>([]);
    const [selectedAddFeature, setSelectedAddFeature] = useState<FeatureType[]>([]);
    const [tab, setTab] = useState<'list' | 'add'>('list');
    const [refresh, setRefresh] = useState<number>(0);

    // State for new feature form
    const [selectedFeatureIds, setSelectedFeatureIds] = useState<number[]>([]);
    const [addFeatureName, setAddFeatureName] = useState<string>('');
    const [addFeatureDescription, setAddFeatureDescription] = useState<string>('');
    const [addFeatureKey, setAddFeatureKey] = useState<string>('');
    const [addFeatureValue, setAddFeatureValue] = useState<number>(0);

    async function removeFeature() {
        if (selectedRemoveFeature.length === 0) {
            toast.error("Please select at least one feature to remove.");
            return;
        }
        const featureIds = selectedRemoveFeature.map(f => f.id);
        const oldFeature = feature.map(f => f.id);
        const newFeature = oldFeature.filter(id => !featureIds.includes(id));
        try {
            await addFeatureToSubcription(subcriptionId, newFeature);
            toast.success("Features removed successfully!");
            setRefresh(prev => prev + 1);
        } catch (error) {
            toast.error("Failed to remove features.");
            console.error(error);
        }
        setRefresh(prev => prev + 1);
    }
    async function handleAddSelectedFeatures() {
        // Logic to add selected features to the subscription
        if (selectedAddFeature.length === 0) {
            toast.error("Please select at least one feature to add.");
            return;
        }
        const featureIds = selectedAddFeature.map(f => f.id);
        const oldFeature = feature.map(f => f.id);
        try {
            await addFeatureToSubcription(subcriptionId, [...oldFeature, ...featureIds]);
            toast.success("Features added successfully!");
            setRefresh(prev => prev + 1);
        } catch (error) {
            toast.error("Failed to add features.");
            console.error(error);
        }
        onClose();
    }

    async function handleAddCustomfeatureList() {
        if (selectedFeatureIds.length > 0) {

            const oldId = feature.map(f => f.id);
            const newfeatureIds = selectedFeatureIds.filter(id => !oldId.includes(id));
            const addlist = await addFeatureToSubcription(subcriptionId, [...oldId, ...newfeatureIds]);
            if (addlist) {
                toast.success("Features added successfully!");
            } else {
                toast.error("Failed to add features.");
            }
        }
        setRefresh(prev => prev + 1);
    }

    async function handleAddCustomFeature() {
        if (addFeatureName.trim() === '' || addFeatureDescription.trim() === '') {
            toast.error("Feature name and description are required.");
            return;
        }
        else if (addFeatureKey.trim() !== '' && isNaN(addFeatureValue)) {
            toast.error("Feature key is required when value is provided.");
            return;
        }
        else if (addFeatureValue && addFeatureKey.trim() === '') {
            toast.error("Feature value must be a valid number.");
            return;
        }
        const newFeatureId = await createAndAddAdvancedFeature(
            [subcriptionId],
            {
                name: addFeatureName,
                description: addFeatureDescription,
                key: addFeatureKey || undefined,
                number: addFeatureValue || undefined
            }
        );
        if (newFeatureId) {
            toast.success("Custom feature created and added successfully!");
            setAddFeatureName('');
            setAddFeatureDescription('');
            setAddFeatureKey('');
            setAddFeatureValue(0);
            setRefresh(prev => prev + 1);
        } else {
            toast.error("Failed to create and add custom feature.");
        }


        setRefresh(prev => prev + 1);
    }

    const removeRowSelectionFeature: TableProps<FeatureType>['rowSelection'] = {
        getCheckboxProps: (record: FeatureType) => ({
            disabled: record.name === 'Disabled data', // Column configuration not to be checked
            name: record.name,
        }),
        onChange: (selectedRowKeys, selectedRows) => {
            setSelectedRemoveFeature(selectedRows);
        }
    };

    const rowSelection: TableProps<FeatureType>['rowSelection'] = {
        getCheckboxProps: (record: FeatureType) => ({
            disabled: record.name === 'Disabled data', // Column configuration not to be checked
            name: record.name,
        }),
        onChange: (selectedRowKeys, selectedRows) => {
            setSelectedAddFeature(selectedRows);
        }
    };

    const customfeatureRowSelection: TableProps<FeatureType>['rowSelection'] = {
        getCheckboxProps: (record: FeatureType) => ({
            disabled: record.name === 'Disabled data', // Column configuration not to be checked
            name: record.name,
        }),
        onChange: (selectedRowKeys, selectedRows) => {
            setSelectedFeatureIds(selectedRows.map(r => r.id));
        }
    };

    useEffect(() => {
        if (subcriptionId) {
            getFeaturesListBySubId(subcriptionId).then((res) => {
                setFeature(res);
            }).catch((error) => {
                console.error("Error fetching features for subscription:", error);
            });
        }
    }, [subcriptionId, refresh]);

    useEffect(() => {
        // Fetch all features to allow adding new ones
        getAllFeatures(0, 0).then((res) => {
            const keyed = res.filter((feature: FeatureType) => feature.key != null && feature.key != undefined && feature.key != '');
            setKeyedFeature(keyed.filter(f => !feature.find((sf) => sf.id === f.id)));
            const existingFeatureIds = new Set(feature.map(f => f.id));
            setAllFeature(res.filter((feature: FeatureType) => !existingFeatureIds.has(feature.id) && feature.key == null));
        }).catch((error) => {
            console.error("Error fetching all features:", error);
        });
    }, [feature, refresh]);


    return (
        <div className="px-2 py-4 flex flex-col gap-4">
            {feature.length === 0 ? (
                <p>No features available for this subscription.</p>
            ) : (
                <div className="flex flex-col gap-1">
                    <div className="flex flex-col gap-4 max-h-[400px] overflow-y-auto">
                        <Table
                            dataSource={feature}
                            columns={tableCollumSub}
                            rowSelection={removeRowSelectionFeature}
                            pagination={{
                                pageSize: 5, position: ['bottomCenter']
                            }} rowKey="id" />

                    </div>
                    <div className="flex gap-2 flex-row justify-end">
                        <button onClick={removeFeature} className="px-4 py-2 rounded-lg bg-red-500 !text-white hover:bg-red-600 transition-colors w-fit">
                            Remove Selected Features
                        </button>
                    </div>
                </div>
            )}
            <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                    <button
                        className={`px-4 py-2 ${tab === 'list' ? 'bg-blue-500 !text-white border-b border-blue-500' : ' bg-gray-200 text-black'} hover:bg-blue-600 cursor-pointer  transition-colors`}
                        onClick={() => setTab('list')}
                    >
                        Add Generic Feature
                    </button>
                    <button
                        className={`px-4 py-2 ${tab === 'add' ? 'bg-blue-500 !text-white border-b border-blue-500' : ' bg-gray-200 text-black'} hover:bg-blue-600 cursor-pointer transition-colors`}
                        onClick={() => setTab('add')}
                    >
                        Add Custom Feature
                    </button>
                </div>
                {
                    tab === 'list' ? (
                        <div className="w-full">
                            <Table
                                dataSource={allFeature}
                                columns={tableCollumAll}
                                rowSelection={rowSelection}
                                rowKey="id"
                                pagination={{ pageSize: 5, position: ['bottomCenter'] }}
                            />
                            <div className="flex gap-2 flex-row justify-between mt-2">
                                <button onClick={() =>
                                    handleAddSelectedFeatures()
                                } className="mt-2 px-4 py-2 rounded-lg bg-green-500 !text-white hover:bg-green-600 transition-colors w-fit">
                                    Add Selected Features
                                </button>
                                <button onClick={onClose} className="ml-2 px-4 py-2 rounded-lg bg-gray-300 hover:bg-gray-400 transition-colors">
                                    Close
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="w-full">
                            <Table
                                dataSource={keyedFeature}
                                columns={tableCollumSub}
                                rowSelection={customfeatureRowSelection}
                                pagination={{
                                    pageSize: 5, position: ['bottomCenter']
                                }} rowKey="id" />
                            <div className="flex gap-2 flex-row justify-end">
                                <button onClick={handleAddCustomfeatureList} className="mt-2 px-4 py-2 rounded-lg bg-green-500 !text-white hover:bg-green-600 transition-colors w-fit">
                                    Add Selected Features
                                </button>
                            </div>
                            <div className="flex flex-col gap-2 p-2 rounded">
                                <div className="text-lg font-semibold mt-2 mb-1">Or Create New Custom Feature</div>
                                <label className="flex flex-col gap-1">
                                    <span>Feature Name:</span>
                                    <input
                                        value={addFeatureName}
                                        onChange={(e) => setAddFeatureName(e.target.value)}
                                        type="text" className="border bg-white rounded px-2 py-1" />
                                </label>
                                <label className="flex flex-col gap-1">
                                    <span>Feature Description:</span>
                                    <input
                                        value={addFeatureDescription}
                                        onChange={(e) => setAddFeatureDescription(e.target.value)}
                                        type="text" className="border bg-white rounded px-2 py-1" />
                                </label>
                                <label className="flex flex-col gap-1">
                                    <span>Feature Key:</span>
                                    <input
                                        value={addFeatureKey}
                                        onChange={(e) => setAddFeatureKey(e.target.value)}
                                        type="text" className="border bg-white rounded px-2 py-1" />
                                </label>
                                <label className="flex flex-col gap-1">
                                    <span>Feature Value:</span>
                                    <input
                                        value={addFeatureValue}
                                        onChange={(e) => setAddFeatureValue(Number(e.target.value))}
                                        type="number" className="border bg-white rounded px-2 py-1" />
                                </label>
                                <div className="flex gap-2 flex-row justify-between mt-2">
                                    <button
                                        onClick={handleAddCustomFeature}
                                        className="px-4 py-2 rounded-lg bg-green-500 !text-white hover:bg-green-600 transition-colors w-fit">
                                        Add Feature
                                    </button>
                                    <button onClick={onClose} className="ml-2 px-4 py-2 rounded-lg bg-gray-300 hover:bg-gray-400 transition-colors">
                                        Close
                                    </button>
                                </div>

                            </div>
                        </div>
                    )
                }


            </div>
        </div >
    );
}

export default EditFeatureOfSubModal;